import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/shared/ui/Button";
import { Card } from "@/shared/ui/Card";
import { Touchable } from "@/shared/ui/Touchable";
import { Row } from "@/shared/ui/Blocks";
import { useModal } from "@/shared/utils/hooks/useModal";
import { ModalFileName } from "@/shared/modals";
import { DimensionWithScores } from "@/features/people/hooks/useDimensionsWithScores";
import { ScorecardRow } from "@/features/scorecards/graphql/generated";
import { DimensionContext } from "@/types/data";
import { AvatarWithInitials } from "@/shared/ui/Avatar";
import { useCandidateCompetencyInsights } from "@/shared/hooks/insights/useCandidateCompetencyInsights";
import { Person } from "@/features/people/graphql/generated";
import { IconName } from "@/shared/ui/Icon";
import ScoreChartGradient from "@/shared/charts/ScoreChart/ScoreChart.gradient";
import IconTag from "@/shared/ui/IconTag";
import { filter } from "lodash";
import { SectionHeader } from "@/shared/ui/SectionHeader";

interface CompetencyInsightsSectionProps {
  dimensions?: DimensionWithScores[];
  context?: DimensionContext;
  person?: Person;
  scorecardRows?: ScorecardRow[];
}

function CandidateCompetencyInsightsSection({
  dimensions = [],
  person,
  scorecardRows,
}: CompetencyInsightsSectionProps) {
  const { t } = useTranslation();
  const [selected, setSelected] = useState("all");
  const [showMore, setShowMore] = useState(false);

  const [openCompetencyModal] = useModal(ModalFileName.DIMENSION_DETAILS);

  const groupedInsights = useCandidateCompetencyInsights({
    dimensions,
    person,
    scorecardRows,
  });

  const allInsights = useMemo(() => {
    return [
      ...(groupedInsights.interviewFocus || []),
      ...(groupedInsights.likelyStrengths || []),
      ...(groupedInsights.developmentPotential || []),
    ];
  }, [groupedInsights]);

  const insightCards = useMemo(() => {
    const filterAndSlice = (filterFn: (insight: any) => boolean) => {
      const filtered = allInsights.filter(filterFn);
      return showMore ? filtered : filtered.slice(0, 6);
    };

    switch (selected) {
      case "interview_focus":
        return filterAndSlice((insight) => insight.type === "interview_focus");
      case "likely_strengths":
        return filterAndSlice((insight) => insight.type === "likely_strengths");
      case "development_potential":
        return filterAndSlice(
          (insight) => insight.type === "development_potential"
        );
      default:
        return showMore ? allInsights : allInsights.slice(0, 6);
    }
  }, [allInsights, selected, showMore]);

  const filterButtons = [
    { key: "all", label: t("competency_insights.all_insights") },
    { key: "interview_focus", label: t("competency_insights.interview_focus") },
    {
      key: "likely_strengths",
      label: t("competency_insights.likely_strengths"),
    },
    {
      key: "development_potential",
      label: t("competency_insights.development_potential"),
    },
  ];

  const cardTags = {
    interview_focus: {
      label: t("competency_insights.interview_focus"),
      color: "text-blue-500",
      icon: "Conversation",
    },
    likely_strengths: {
      label: t("competency_insights.likely_strengths"),
      color: "text-green-500",
      icon: "Strength",
    },
    development_potential: {
      label: t("competency_insights.development_potential"),
      color: "text-orange-500",
      icon: "Plant",
    },
  };

  return (
    <div className="w-full flex flex-col justify-center items-center mt-8">
      <SectionHeader
        title={t("profile.competency_insights_title")}
        subtitle={t("profile.competency_insights_description", {
          context: t("context_label.candidate_posessive"),
        })}
      />
      <Row className="gap-4 items-center mb-8">
        {filterButtons.map((filter) => (
          <Button
            key={filter.key}
            onClick={() => setSelected(filter.key)}
            variant={selected === filter.key ? "default" : "outline"}
            size="md"
          >
            {filter.label}
          </Button>
        ))}
      </Row>
      <div className="gap-4 columns-1 md:columns-2 lg:columns-3">
        {insightCards.map((insight, index) => {
          const initialCompetency = dimensions.find(
            (d) => d.id === insight.dimension.id
          );

          const cardTag = cardTags[insight.type as keyof typeof cardTags];

          return (
            <Touchable
              key={`insight-${insight.dimension.id}-${index}`}
              className="mb-8"
              onClick={() =>
                openCompetencyModal({
                  dimension: initialCompetency,
                  allDimensions: dimensions,
                  allInsights: insightCards,
                  context: "candidate",
                  tags: insight.tags,
                  insight,
                })
              }
            >
              <Card className="p-4 break-inside-avoid bg-gray-50 max-w-[300px]">
                <IconTag
                  label={cardTag.label}
                  color={cardTag.color}
                  icon={cardTag.icon as IconName}
                />
                <h5 className="text-base leading-6 mb-2 max-w-[95%]">
                  {insight.title}
                </h5>
                <p className="text-sm leading-5 font-light text-gray-600">
                  {insight.description}
                </p>

                <Card className="p-4 mt-4 min-h-[100px]">
                  <h5 className="mb-2 !text-sm !text-semibold">
                    {insight.dimension.title}
                  </h5>
                  <ScoreChartGradient
                    scores={filter(
                      insight.dimension.peopleScores,
                      (score) => score.person?.id !== person?.id
                    )}
                    score={insight.dimension.score}
                    averageScore={insight.dimension.averageScore?.value}
                    showArea
                    areaColor="blue"
                    renderScore={(score) => (
                      <AvatarWithInitials
                        size="sm"
                        avatar={person?.avatar || ""}
                        initials={person?.initials || ""}
                      />
                    )}
                  />
                </Card>
              </Card>
            </Touchable>
          );
        })}
      </div>
      {insightCards.length === 0 && (
        <div className="w-full text-center">
          <p className="text-gray-500">
            {t("competency_insights.no_insights_available")}
          </p>
        </div>
      )}
      {allInsights.length > 6 && (
        <div className="mt-4">
          <Button variant="outline" onClick={() => setShowMore(!showMore)}>
            {showMore ? t("common.show_less") : t("common.show_more")}
          </Button>
        </div>
      )}
    </div>
  );
}

export { CandidateCompetencyInsightsSection };
